import { cn } from "@/lib/utils";
import { Compass, X } from "lucide-react";
import {
  WorkspaceCardFooterTitleRow,
  WorkspaceCardFooterTypeIcon,
} from "../WorkspaceCardFooterTypeIcon";
import { WorkspaceGridCard } from "../WorkspaceGridCard";
import {
  gridCardFooterMetaRowClassName,
  gridCardFooterSubtitleClassName,
  gridCardFooterTitleClassName,
} from "../constants";

/** Rust accent from the onboarding `_co_onb` palette (`--co-rust`). */
const RUST = "#B86E49";

export interface FinishOnboardingCardProps {
  onStart: () => void;
  onDismiss: () => void;
  className?: string;
}

/**
 * Promo card pinned to the front of the Home "Recents" grid for users
 * who skipped or haven't finished onboarding. Dimensions mirror
 * `NewCanvasCard` / `CanvasCard` so the grid rhythm stays intact.
 */
export function FinishOnboardingCard({
  onStart,
  onDismiss,
  className,
}: FinishOnboardingCardProps) {
  const preview = (
    <>
      <button
        type="button"
        aria-label="Don't show again"
        title="Don't show again"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-background/70 text-muted-foreground backdrop-blur transition-colors hover:bg-background hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="absolute inset-0 flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#F5F0E5] to-[#EBE3D1] dark:from-[#221f19] dark:to-[#19160f]">
        <div
          aria-hidden
          className="pointer-events-none absolute h-24 w-24 rounded-full blur-2xl"
          style={{ background: `${RUST}3D` }}
        />
        <Compass
          className="relative h-10 w-10 transition-transform duration-200 group-hover:scale-105"
          style={{ color: RUST }}
          strokeWidth={1.75}
        />
      </div>
    </>
  );

  const footer = (
    <div className="min-w-0 flex-1">
      <WorkspaceCardFooterTitleRow
        icon={<WorkspaceCardFooterTypeIcon icon={Compass} />}
      >
        <p className={gridCardFooterTitleClassName}>Finish onboarding</p>
      </WorkspaceCardFooterTitleRow>
      <p
        className={cn(
          gridCardFooterMetaRowClassName,
          "leading-snug",
          gridCardFooterSubtitleClassName,
        )}
      >
        Pick up the guided tour on a fresh canvas
      </p>
    </div>
  );

  return (
    <WorkspaceGridCard
      aria-label="Finish onboarding"
      {...({ alt: "onboarding-finish-card" } as Record<string, string>)}
      onClick={onStart}
      preview={preview}
      footer={footer}
      interactive
      previewClassName="border-[#B86E49]/25 group-hover:border-[#B86E49]/70"
      className={className}
    />
  );
}
